import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import as9120Logo from "../assets/img/as9120-certification.png";
import { classNames } from "../common/functions";

type AS9120BadgeProps = {
  /** Tailwind classes for the wrapper (spacing, alignment). */
  className?: string;
  /** Tailwind classes controlling the logo size (defaults to a small height). */
  imgClassName?: string;
};

/**
 * Trust badge advertising Kono's AS9120 certification (the aerospace quality
 * management standard for distributors). Shown alongside the Kono Guarantee in
 * search results, on product pages, and in the cart to build buyer confidence.
 * Clicking it opens a short explainer.
 */
export const AS9120Badge = ({ className, imgClassName }: AS9120BadgeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title="AS9120 Certification — aerospace quality management for distributors. Click to learn more."
        aria-label="AS9120 Certification. Learn more."
        className={classNames(
          "inline-flex items-center focus:outline-none",
          className,
        )}
      >
        <img
          src={as9120Logo}
          alt="AS9120 Certification"
          className={classNames("h-6 w-auto", imgClassName)}
        />
      </button>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                <img
                  src={as9120Logo}
                  alt="AS9120 Certification"
                  className="h-10 w-auto"
                />
                <Dialog.Title className="mt-4 text-lg font-semibold text-gray-900">
                  AS9120 Certification
                </Dialog.Title>
                <p className="mt-3 text-sm text-gray-600">
                  AS9120 Certification pending approval. Kono&rsquo;s quality
                  management system is built to AS9120B, the aerospace standard
                  for stockist distributors, covering traceability and records
                  controls. Every order ships with full mill test reports
                  traceable to the original manufacturer, unless otherwise
                  indicated. All Kono supply partners hold AS9100, AS9120 or
                  ISO9001 certifications.
                </p>
                <p className="mt-3 text-sm text-gray-600">
                  Questions or comments? Please write to support@kono.ai
                </p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-bold text-white hover:bg-slate-900 focus:bg-slate-900"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
